import React from 'react';
import moduleChangelog from './module.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { ModuleNodeState, NodeConfig, NodeField, NodeFieldType, NodeInputType, NodeOutput, NodeType } from '../../';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';
import { useNodeOutline } from '@common/hooks/useNodeOutline';

export const config: NodeConfig = {
  version: '1.0',
  title: 'Module',
  trigger: true,
  identifier: 'Module',
  command_id: 'workflow.module',
  background: '#00E3AA',
  icon: 'module',
  changelog: moduleChangelog,
  anchors: [
    {
      label: 'Received',
      id: 'workflow.module.start',
      type: 'source' as HandleType,
      variant: 'execution',
    },
    {
      label: 'Data',
      id: 'workflow.module.data',
      type: 'source' as HandleType,
      variant: 'data',
      isHandle: true,
    },
  ],
};

export const state: ModuleNodeState = {
  description: '',
  parameters: [],
};

export const fields: NodeField[] = [
  {
    id: 'description',
    tab: '1',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Description',
    description: 'Use this field to describe what your modules is used for.',
    placeholder: 'Description',
  },
  {
    id: 'parameters',
    tab: '1',
    type: NodeFieldType.ModuleParameter,
    label: 'Parameters',
    description:
      'Create input fields that can be used to pass data into your module. Each field is given a name, a type and a description of what is is used for.',
  },
];

export const output: NodeOutput = {
  default: {
    workflow: {
      module: {},
    },
  },
  dynamic: null,
};

export const Module: React.FC<NodeType> = ({ id, selected, data }) => {
  const { background, anchors, icon } = config;

  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'workflow.module') return null;

  const isToolbarVisible = Boolean(isNodeError);

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={`Module`} icon={icon} background={background} status={nodeStatus} />
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
